import { v1TraitNarrative } from "@/features/assessment-v2/v1port/nudge";

/**
 * "What you told us about yourself" — the ported v1 trait set, shown back to
 * the learner in v1's own superpower language.
 *
 * Explainability only. These are self-report answers, so they never decide a
 * career match; they colour the story and, through a hard-capped nudge, can
 * break a near-tie. The section says so plainly rather than implying the
 * numbers ranked anything.
 */
export function TraitStrengths({ part2 }: { part2: Record<string, unknown> }) {
  const narrative = v1TraitNarrative(part2);
  if (!narrative || narrative.strengths.length === 0) return null;

  return (
    <section className="mt-4 rounded-2xl border border-border bg-card p-5">
      <h2 className="text-sm font-medium text-foreground">What you told us about yourself</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Your own words, in your own voice. These shape how we describe you — your
        matches come from the choices you made, not from these.
      </p>
      <ul className="mt-4 space-y-3">
        {narrative.strengths.map(({ trait, score, superpower }) => (
          <li key={trait}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-foreground">{trait}</span>
              <span className="text-xs text-muted-foreground">{score} out of 100</span>
            </div>
            <div
              className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted"
              role="img"
              aria-label={`${trait}: ${score} out of 100`}
            >
              <div className="h-full rounded-full bg-primary" style={{ width: `${score}%` }} />
            </div>
            {superpower && (
              <p className="mt-1.5 text-sm text-muted-foreground">{superpower}</p>
            )}
          </li>
        ))}
      </ul>
      {narrative.coverage < 1 && (
        <p className="mt-4 text-xs text-muted-foreground">
          You answered {Math.round(narrative.coverage * 100)}% of these, so we've
          kept their influence small.
        </p>
      )}
    </section>
  );
}
